import React, { useState, useEffect, useMemo, useRef } from 'react';
import {
    FormControl,
    InputLabel,
    OutlinedInput,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
    ListSubheader,
    CircularProgress,
    Box,
    Button,
    TextField,
    Typography,
} from '@mui/material';
import { useDataProvider } from 'src/mtska-frontend-data-provider';
import { useTranslation } from 'react-i18next';
import { getFullscreenPortalContainer } from 'src/mtska-frontend-app-component/utils/fullscreenPortalContainer';

const defaultFilters = {};

const normalizeDistinct = (distinct) => {
    if (distinct == null || distinct === '') return [];
    return Array.isArray(distinct) ? distinct : [distinct];
};

const dedupeOptionRows = (list, valueKey) => {
    const rows = list ?? [];
    const seen = new Set();
    return rows.filter((row) => {
        const v = row?.[valueKey];
        if (v == null || v === '') return false;
        if (seen.has(v)) return false;
        seen.add(v);
        return true;
    });
};

/** Da value (come InputAutocomplete): null = tutte; { [valueKey]: string | string[] } = cosa flaggare */
const selectionKeysFromValue = (value, valueKey) => {
    if (value == null) return null;
    const raw = value[valueKey];
    if (raw == null || raw === '') return null;
    if (Array.isArray(raw)) return raw.length > 0 ? raw : null;
    return [raw];
};

const InputFilterMultiCheckboxCore = ({
    valueKey = 'name',
    value = null,
    onChange,
    labelkey = 'name',
    label = 'Select',
    name,
    disabled,
    style,
    size = 'small',
    listMaxHeight = 360,
    searchPlaceholder,
    options,
    loading,
}) => {
    const { t } = useTranslation();
    const labelId = name ?? `multi-checkbox-${valueKey}`;

    const allKeys = useMemo(() => options.map((o) => o[valueKey]), [options, valueKey]);

    const [selectedKeys, setSelectedKeys] = useState([]);
    const [menuSearch, setMenuSearch] = useState('');
    const [selectOpen, setSelectOpen] = useState(false);
    const searchInputRef = useRef(null);

    const visibleOptions = useMemo(() => {
        const q = menuSearch.trim().toLowerCase();
        if (!q) return options;
        return options.filter((o) => {
            let text = '';
            if (Array.isArray(labelkey)) {
                text = `${o[labelkey[0]] ?? ''} ${o[labelkey[1]] ?? ''}`.trim();
            } else {
                text = String(o[labelkey] ?? '');
            }
            const keyStr = String(o[valueKey] ?? '');
            return `${text} ${keyStr}`.toLowerCase().includes(q);
        });
    }, [options, menuSearch, valueKey, labelkey]);

    const keysFromValue = selectionKeysFromValue(value, valueKey);
    const valueSyncKey =
        keysFromValue == null ? '__all__' : JSON.stringify([...keysFromValue].sort());

    const allKeysKey = allKeys.join('|');

    const getLabel = (option) => {
        if (!option) return '';
        if (Array.isArray(labelkey)) {
            return `${option[labelkey[0]] ?? ''} ${option[labelkey[1]] ?? ''}`.trim();
        }
        return option[labelkey] ?? '';
    };

    useEffect(() => {
        if (!allKeys.length) {
            setSelectedKeys([]);
            return;
        }
        if (keysFromValue == null) {
            setSelectedKeys(allKeys);
            return;
        }
        const next = keysFromValue.filter((v) => allKeys.includes(v));
        setSelectedKeys(next.length ? next : allKeys);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- valueSyncKey, allKeysKey
    }, [valueSyncKey, allKeysKey]);

    const publishSelection = (selectionValue, uiKeys) => {
        setSelectedKeys(uiKeys);
        onChange(selectionValue, { allKeys, valueKey });
    };

    const onSelectChange = (event) => {
        const raw = event.target.value;
        const next = typeof raw === 'string' ? raw.split(',') : raw;

        if (!next.length) {
            publishSelection(null, [...allKeys]);
            return;
        }
        if (next.length === allKeys.length) {
            publishSelection(null, [...allKeys]);
            return;
        }
        publishSelection({ [valueKey]: next }, next);
    };

    const handleSelectClose = () => {
        setSelectOpen(false);
        setMenuSearch('');
    };

    const selectOnly = (key) => {
        publishSelection({ [valueKey]: [key] }, [key]);
        handleSelectClose();
    };

    const selectedLabelsText = (selected) =>
        selected
            .map((key) => {
                const o = options.find((x) => x[valueKey] === key);
                return getLabel(o) || key;
            })
            .join(', ');

    const allOptionsSelected =
        allKeys.length > 0 && allKeys.every((k) => selectedKeys.includes(k));
    const someButNotAllSelected =
        !allOptionsSelected &&
        allKeys.length > 0 &&
        selectedKeys.some((k) => allKeys.includes(k));

    const renderSelected = (selected) => {
        if (!allKeys.length) return '';
        const body =
            selected.length === allKeys.length ? t('all') : selectedLabelsText(selected);
        if (body === '') return '';
        return (
            <Box
                component="span"
                sx={{
                    flex: 1,
                    minWidth: 0,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                }}
            >
                {body}
            </Box>
        );
    };

    return (
        <FormControl sx={{ minWidth: 0, ...(style || {}) }} size={size} disabled={disabled || loading} fullWidth>
            <InputLabel id={labelId} shrink>
                {label}
            </InputLabel>
            <Box sx={{ position: 'relative', width: '100%', minWidth: 0 }}>
                <Select
                    name={name}
                    labelId={labelId}
                    multiple
                    fullWidth
                    open={selectOpen}
                    onOpen={() => {
                        setSelectOpen(true);
                        requestAnimationFrame(() => {
                            searchInputRef.current?.focus();
                        });
                    }}
                    onClose={handleSelectClose}
                    value={selectedKeys}
                    onChange={onSelectChange}
                    input={<OutlinedInput label={label} />}
                    renderValue={renderSelected}
                    sx={{
                        maxWidth: '100%',
                        minWidth: 0,
                        '& .MuiSelect-select': {
                            display: 'flex',
                            alignItems: 'center',
                            minWidth: 0,
                            overflow: 'hidden',
                        },
                    }}
                    MenuProps={{
                        PaperProps: { style: { maxHeight: listMaxHeight } },
                        autoFocus: false,
                        container: getFullscreenPortalContainer,
                        MenuListProps: {
                            sx: { paddingTop: 0 },
                        },
                    }}
                >
                {!loading && (
                    <ListSubheader
                        sx={{
                            py: 0.75,
                            px: 0.5,
                            lineHeight: 1.25,
                            backgroundColor: 'var(--app-maincanvas-border-color)',
                            position: 'sticky',
                            top: 0,
                            zIndex: 2,
                            borderBottom: 1,
                            borderColor: 'divider',
                        }}
                        onMouseDown={(e) => e.stopPropagation()}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                mb: 0.75,
                            }}
                        >
                            <Checkbox
                                size="small"
                                checked={allOptionsSelected}
                                indeterminate={someButNotAllSelected}
                                disabled={allKeys.length === 0}
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    if (allKeys.length === 0) return;
                                    publishSelection(null, [...allKeys]);
                                }}
                            />
                            <Typography
                                variant="body2"
                                component="span"
                                sx={{ fontWeight: 600, flex: 1, minWidth: 0 }}
                            >
                                {label}
                            </Typography>
                        </Box>
                        <TextField
                            inputRef={searchInputRef}
                            size="small"
                            fullWidth
                            placeholder={searchPlaceholder ?? t('Type to search')}
                            value={menuSearch}
                            onChange={(e) => setMenuSearch(e.target.value)}
                            onClick={(e) => e.stopPropagation()}
                            onKeyDown={(e) => {
                                if (e.key === 'Escape') return;
                                e.stopPropagation();
                            }}
                            sx={{
                                '& .MuiOutlinedInput-input': {
                                    backgroundColor: 'white',
                                },
                            }}
                        />
                    </ListSubheader>
                )}
                {loading && (
                    <MenuItem disabled>
                        <Box display="flex" alignItems="center" gap={1}>
                            <CircularProgress size={18} />
                        </Box>
                    </MenuItem>
                )}
                {!loading && visibleOptions.length === 0 && (
                    <MenuItem disabled>{t('No options found')}</MenuItem>
                )}
                {!loading &&
                    visibleOptions.map((o) => {
                        const v = o[valueKey];
                        return (
                            <MenuItem key={v} value={v} dense>
                                <Checkbox size="small" checked={selectedKeys.indexOf(v) > -1} />
                                <ListItemText primary={getLabel(o)} sx={{ flex: '1 1 auto', minWidth: 0, mr: 0.5 }} />
                                <Button
                                    type="button"
                                    size="small"
                                    variant="text"
                                    sx={{
                                        flexShrink: 0,
                                        minWidth: 0,
                                        px: 0.75,
                                        py: 0.25,
                                        fontSize: '0.7rem',
                                        lineHeight: 1.2,
                                        textTransform: 'none',
                                    }}
                                    onMouseDown={(e) => e.stopPropagation()}
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        selectOnly(v);
                                    }}
                                >
                                    {t('only')}
                                </Button>
                            </MenuItem>
                        );
                    })}
                </Select>
                {loading && (
                    <CircularProgress
                        size={14}
                        thickness={4.5}
                        sx={{
                            position: 'absolute',
                            right: (theme) => theme.spacing(4),
                            top: '50%',
                            marginTop: '-7px',
                            zIndex: 2,
                            pointerEvents: 'none',
                            color: 'grey.600',
                        }}
                    />
                )}
            </Box>
        </FormControl>
    );
};

const InputFilterMultiCheckboxFromServer = ({
    dataProvider,
    dataProviderName,
    valueKey = 'name',
    value = null,
    onChange,
    labelkey = 'name',
    filters = defaultFilters,
    project = [],
    distinct = [],
    label = 'Select',
    name,
    disabled,
    style,
    size = 'small',
    listMaxHeight = 360,
    perPage = 2000,
    searchPlaceholder,
}) => {
    const dp = dataProvider ?? dataProviderName;
    const { items: optionRows, loading, loadAll } = useDataProvider(dp);

    const options = useMemo(
        () => dedupeOptionRows(optionRows, valueKey),
        [optionRows, valueKey],
    );

    const hydrateRequestKey = JSON.stringify({
        items: filters?.items ?? [],
        project,
        distinct: normalizeDistinct(distinct),
    });

    useEffect(() => {
        const compoundfilters = {
            items: filters?.items ?? [],
            project,
            distinct,
        };
        loadAll(compoundfilters, {}, 1, perPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- dp, perPage, hydrateRequestKey
    }, [dp, perPage, hydrateRequestKey]);

    return (
        <InputFilterMultiCheckboxCore
            valueKey={valueKey}
            value={value}
            onChange={onChange}
            labelkey={labelkey}
            label={label}
            name={name}
            disabled={disabled}
            style={style}
            size={size}
            listMaxHeight={listMaxHeight}
            searchPlaceholder={searchPlaceholder}
            options={options}
            loading={loading}
        />
    );
};

const InputFilterMultiCheckboxStatic = ({
    staticOptions,
    valueKey = 'name',
    value = null,
    onChange,
    labelkey = 'name',
    label = 'Select',
    name,
    disabled,
    style,
    size = 'small',
    listMaxHeight = 360,
    searchPlaceholder,
}) => {
    const options = useMemo(
        () => dedupeOptionRows(staticOptions, valueKey),
        [staticOptions, valueKey],
    );

    return (
        <InputFilterMultiCheckboxCore
            valueKey={valueKey}
            value={value}
            onChange={onChange}
            labelkey={labelkey}
            label={label}
            name={name}
            disabled={disabled}
            style={style}
            size={size}
            listMaxHeight={listMaxHeight}
            searchPlaceholder={searchPlaceholder}
            options={options}
            loading={false}
        />
    );
};

/**
 * Filtro multi-select a checkbox. `value` null = tutte (nessun filtro); sottoinsieme → `{ [valueKey]: string[] }`.
 * Con `staticOptions` (array) le opzioni non vengono caricate dal server.
 */
const InputFilterMultiCheckbox = (props) => {
    const { staticOptions, ...serverProps } = props;
    if (staticOptions != null) {
        return <InputFilterMultiCheckboxStatic {...props} />;
    }
    return <InputFilterMultiCheckboxFromServer {...serverProps} />;
};

export default InputFilterMultiCheckbox;
